import React from "react";
import {
  Box,
  Button,
  Icon,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
  Text,
} from "@salestrak/ui";

import type { ObjectAccessLevel } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// The one control on a share row. It reads like a select — the current
// tier and an up/down chevron on the outline rung — but it is a Menu: the
// tiers are a radio group, and the row's other verbs (reset an override,
// remove the access) ride the same popup so a row never needs a second
// control beside the level.

export const ACCESS_LEVEL_OPTIONS: ReadonlyArray<{
  value: ObjectAccessLevel;
  label: string;
  description: string;
}> = [
  // One parallel phrase each: what the tier lets someone do, and no more.
  { value: "full_edit", label: "Full edit", description: "Edit and share" },
  { value: "edit", label: "Edit", description: "Edit and comment" },
  { value: "comment", label: "Comment", description: "Comment only" },
  { value: "view", label: "View", description: "View only" },
];

export const ACCESS_LEVEL_LABEL: Record<ObjectAccessLevel, string> = {
  full_edit: "Full edit",
  edit: "Edit",
  comment: "Comment",
  view: "View",
};

export function AccessLevelMenu({
  value,
  onChange,
  onRemove,
  onReset,
  inherited = false,
  disabled = false,
  removeLabel = "Remove access",
}: Omittable<{
  value: ObjectAccessLevel;
  onChange: (level: ObjectAccessLevel) => void;
  // A switched-off row keeps its tier but cannot change it until it is
  // switched back on; the menu reads the remembered value, disabled.
  disabled?: boolean;
  // Omit when the row's access cannot be removed here (a team the item
  // carries on its Teams property comes off there, not in this dialog).
  onRemove?: () => void;
  // Present on a member row that overrides its team's tier: deletes the
  // member's own row so the team's tier applies again.
  onReset?: () => void;
  // The tier comes from the team the row sits under, not from the row
  // itself — read in muted ink so an override stands out against it.
  inherited?: boolean;
  removeLabel?: string;
}>) {
  return (
    <Menu>
      <MenuTrigger
        disabled={disabled}
        render={<Button variant="outline" size="sm" shape="square" />}
      >
        <Text size="sm" variant={inherited ? "muted" : undefined}>
          {ACCESS_LEVEL_LABEL[value]}
        </Text>
        <Icon name="chevrons-up-down" size="sm" color="faint" />
      </MenuTrigger>
      <MenuPopup align="end" width="action">
        <MenuRadioGroup
          value={value}
          onValueChange={(next) => {
            if (typeof next === "string" && next !== value) {
              onChange(next as ObjectAccessLevel);
            }
          }}
        >
          {/* A tier is a choice, not a toggle: picking one closes the popup
              the way a select would. */}
          {ACCESS_LEVEL_OPTIONS.map((opt) => (
            <MenuRadioItem key={opt.value} value={opt.value} closeOnClick>
              <TierLine label={opt.label} description={opt.description} />
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        {onReset || onRemove ? <MenuSeparator /> : null}
        {onReset ? <MenuItem onClick={onReset}>Reset to team</MenuItem> : null}
        {onRemove ? <MenuItem onClick={onRemove}>{removeLabel}</MenuItem> : null}
      </MenuPopup>
    </Menu>
  );
}

// A tier's two lines: its name, and what it lets someone do. Only a tier
// carries a description; a verb's label already says what happens.
function TierLine({ label, description }: { label: string; description: string }) {
  return (
    <Box direction="column" minWidth={0} paddingY="2xs">
      <Text size="sm">{label}</Text>
      <Text size="xs" variant="muted">
        {description}
      </Text>
    </Box>
  );
}

// The one control over a whole access list: every tier as a bulk set, and
// the bulk remove under a separator. Sits on the list's section label so
// the destructive verb is near what it affects and never beside a search.
export function EditAllMenu({
  onSetAll,
  onRemoveAll,
  setAllLabel = "Set everyone to",
}: Omittable<{
  onSetAll: (level: ObjectAccessLevel) => void;
  onRemoveAll: () => void;
  // The bulk group's heading — who or what the tiers apply to.
  setAllLabel?: string;
}>) {
  return (
    <Menu>
      <MenuTrigger render={<Button variant="ghost" size="sm" shape="square" />}>
        <Text size="xs">Edit all</Text>
        <Icon name="chevrons-up-down" size="sm" color="faint" />
      </MenuTrigger>
      <MenuPopup align="end" width="action">
        <MenuGroup>
          <MenuGroupLabel>{setAllLabel}</MenuGroupLabel>
          {ACCESS_LEVEL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} onClick={() => onSetAll(opt.value)}>
              {opt.label}
            </MenuItem>
          ))}
        </MenuGroup>
        <MenuSeparator />
        <MenuItem variant="destructive" onClick={onRemoveAll}>
          Remove all
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}
